import { loadPdfDocument } from './pdfRender.js';

// Extract visible text of a page as an ordered list of lines.
async function extractPageLines(doc, pageIndex) {
  const page = await doc.getPage(pageIndex + 1);
  const content = await page.getTextContent({ includeMarkedContent: false });
  // Group text items into rows by their (rounded) y baseline.
  const rows = new Map();
  for (const it of content.items) {
    if (typeof it.str !== 'string' || it.str.length === 0) continue;
    const y = Math.round(it.transform[5]);
    if (!rows.has(y)) rows.set(y, []);
    rows.get(y).push({ x: it.transform[4], str: it.str });
  }
  // Rows top-to-bottom (PDF y grows upward), items left-to-right.
  const ys = [...rows.keys()].sort((a, b) => b - a);
  const lines = [];
  for (const y of ys) {
    const text = rows
      .get(y)
      .sort((a, b) => a.x - b.x)
      .map((i) => i.str)
      .join('')
      .replace(/\s+/g, ' ')
      .trim();
    if (text) lines.push(text);
  }
  return lines;
}

// Split a string into tokens (words + whitespace runs kept).
function tokenize(s) {
  return s.split(/(\s+)/).filter((x) => x.length > 0);
}

// Length of the longest common subsequence of two arrays.
function lcsLength(a, b) {
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  return dp[0][0];
}

// Generic LCS diff over two arrays -> rows of { type, a, b } (raw elements).
function lcsDiff(a, b, eq) {
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = eq(a[i], b[j])
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const rows = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (eq(a[i], b[j])) {
      rows.push({ type: 'same', a: a[i], b: b[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      rows.push({ type: 'removed', a: a[i], b: null });
      i++;
    } else {
      rows.push({ type: 'added', a: null, b: b[j] });
      j++;
    }
  }
  while (i < n) rows.push({ type: 'removed', a: a[i++], b: null });
  while (j < m) rows.push({ type: 'added', a: null, b: b[j++] });
  return rows;
}

// Word-level diff of two strings -> [{ type:'same'|'removed'|'added', text }].
export function diffWords(a, b) {
  const wa = tokenize(a);
  const wb = tokenize(b);
  return lcsDiff(wa, wb, (x, y) => x === y).map((r) => ({
    type: r.type,
    text: r.type === 'added' ? r.b : r.a,
  }));
}

// 0..1 similarity of two lines based on shared (non-space) tokens.
function lineSimilarity(a, b) {
  const wa = tokenize(a).filter((x) => x.trim().length > 0);
  const wb = tokenize(b).filter((x) => x.trim().length > 0);
  if (wa.length === 0 && wb.length === 0) return 1;
  const common = lcsLength(wa, wb);
  return (2 * common) / (wa.length + wb.length || 1);
}

const SIMILAR_THRESHOLD = 0.45;

/**
 * Diff two arrays of page-tagged line entries ({ text, page }).
 * One global LCS over the whole document so cross-page drift never
 * misaligns the comparison.
 * Output rows: { type, a: entry|null, b: entry|null, words? }
 *   type: 'same' | 'removed' | 'added' | 'changed'
 *   'changed' rows carry `words` = word-level diff between a.text/b.text.
 */
export function diffEntries(entriesA, entriesB) {
  const base = lcsDiff(entriesA, entriesB, (x, y) => x.text === y.text);

  // The exact-match LCS leaves blocks of non-'same' rows. Within such a
  // block the removed and added lines are usually interleaved (-+-+),
  // and a line edited in place (bullet added, punctuation changed,
  // moved across a page break) ends up offset from its counterpart.
  // Re-align each block: take the removed lines and the added lines and
  // run a second LCS over them keyed on line *similarity*, so a small
  // edit becomes a single 'changed' row instead of a red+green pair.
  const out = [];
  let k = 0;
  while (k < base.length) {
    if (base[k].type === 'same') {
      out.push(base[k]);
      k++;
      continue;
    }
    const block = [];
    while (k < base.length && base[k].type !== 'same') {
      block.push(base[k]);
      k++;
    }
    const removed = block.filter((r) => r.type === 'removed');
    const added = block.filter((r) => r.type === 'added');
    if (removed.length === 0 || added.length === 0) {
      for (const r of block) out.push(r);
      continue;
    }
    const aligned = lcsDiff(
      removed,
      added,
      (x, y) => lineSimilarity(x.a.text, y.b.text) >= SIMILAR_THRESHOLD
    );
    for (const al of aligned) {
      if (al.type === 'same') {
        const ea = al.a.a;
        const eb = al.b.b;
        out.push({ type: 'changed', a: ea, b: eb, words: diffWords(ea.text, eb.text) });
      } else if (al.type === 'removed') {
        out.push(al.a);
      } else {
        out.push(al.b);
      }
    }
  }
  return out;
}

/**
 * Compare two PDFs as whole documents (page-by-page alignment drifts as
 * soon as content shifts across a page break).
 * onProgress(done, total) fires while extracting pages.
 * Returns { rows, changed, countA, countB, linesA, linesB }.
 *   rows: see diffEntries; a/b entries carry { text, page }.
 */
export async function comparePdfs(bytesA, bytesB, onProgress) {
  const docA = await loadPdfDocument(bytesA);
  const docB = await loadPdfDocument(bytesB);
  const total = docA.numPages + docB.numPages;
  let done = 0;

  const entriesA = [];
  for (let i = 0; i < docA.numPages; i++) {
    const lines = await extractPageLines(docA, i);
    for (const text of lines) entriesA.push({ text, page: i });
    onProgress?.(++done, total);
  }

  const entriesB = [];
  for (let i = 0; i < docB.numPages; i++) {
    const lines = await extractPageLines(docB, i);
    for (const text of lines) entriesB.push({ text, page: i });
    onProgress?.(++done, total);
  }

  const rows = diffEntries(entriesA, entriesB);
  const changed = rows.filter((r) => r.type !== 'same').length;

  return {
    rows,
    changed,
    countA: docA.numPages,
    countB: docB.numPages,
    linesA: entriesA.length,
    linesB: entriesB.length,
  };
}
